import jwtDecode from 'jwt-decode';
import http from './httpService';

const apiEndpoint = `/client/auth`;
const accessTokenKey = 'accessToken';
const refreshTokenKey = 'refreshToken';

// Initialize auth state on app load
const accessToken = localStorage.getItem(accessTokenKey);
if (accessToken) http.setJwt(accessToken);

/**
 * Logs in a user with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} User data
 */
export async function login(email, password) {
  try {
    const { data } = await http.post(apiEndpoint, { email, password });
    
    // Store tokens
    localStorage.setItem(accessTokenKey, data.accessToken);
    localStorage.setItem(refreshTokenKey, data.refreshToken);
    
    // Set default auth header
    http.setJwt(data.accessToken);
    
    return {
      user: data.user,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken
    };
  } catch (error) {
    // Handle specific error cases
    if (error.response) {
      const message =
        (typeof error.response.data === 'string'
          ? error.response.data
          : (error.response.data && error.response.data.error)) ||
        'Login failed. Please try again.';
      const err = new Error(message);
      err.response = error.response;
      throw err;
    }
    throw new Error('Login failed. Please try again.');
  }
}

/**
 * Logs out the current user
 * @param {boolean} [skipApiCall=false] - Whether to skip the API call to revoke tokens
 */
export async function logout(skipApiCall = false) {
  try {
    // Try to revoke the refresh token if we have one
    const refreshToken = localStorage.getItem(refreshTokenKey);
    if (!skipApiCall && refreshToken) {
      await http.post(`${apiEndpoint}/revoke-token`, { token: refreshToken });
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Continue with local logout even if API call fails
  } finally {
    // Clear tokens and user data
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);
    http.removeJwt();
    
    // Redirect to login page
    window.location.href = '/client/login';
  }
}

/**
 * Gets the current authenticated user from the access token
 * @returns {Object|null} The current user or null if not authenticated
 */
export function getCurrentUser() {
  try {
    const token = localStorage.getItem(accessTokenKey);
    if (!token) return null;
    
    const decoded = jwtDecode(token);
    
    // Check if token is expired
    if (decoded.exp * 1000 < Date.now()) {
      logout(true);
      return null;
    }
    
    return {
      id: decoded.id,
      contactId: decoded.contactId,
      role: decoded.role,
      department: decoded.department,
      isEmployee: decoded.isEmployee,
      type: decoded.type,
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Checks if the current user is authenticated
 * @returns {boolean} True if authenticated, false otherwise
 */
export function isAuthenticated() {
  return !!getCurrentUser();
}

/**
 * Checks if the current user has the required role
 * @param {string|string[]} requiredRole - The required role(s)
 * @returns {boolean} True if user has the required role
 */
export function hasRole(requiredRole) {
  const user = getCurrentUser();
  if (!user) return false;
  
  if (Array.isArray(requiredRole)) {
    return requiredRole.includes(user.role);
  }
  
  return user.role === requiredRole;
}

/**
 * Gets the access token
 * @returns {string|null} The access token or null if not available
 */
export function getAccessToken() {
  return localStorage.getItem(accessTokenKey);
}

/**
 * Gets the refresh token
 * @returns {string|null} The refresh token or null if not available
 */
export function getRefreshToken() {
  return localStorage.getItem(refreshTokenKey);
}

const authService = {
  login,
  logout,
  getCurrentUser,
  isAuthenticated,
  hasRole,
  getAccessToken,
  getRefreshToken,
};

export default authService;
